import { IHeaderBidder } from '~/src/dash/api/console-api';
import { createUrl, restGet } from '~/src/dash/api/rest';
import { IBidderGroup } from '~/src/dash/api/bidder-group';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { checkUserInfoMasked } from '~/src/dash/utils/roles';
import { IBidderLabel } from '~/src/dash/api/filters/bidderLabels';
import { PID } from '~/src/dash/utils/publishers';
import {
  fetchSmallBidderGroupsOptionsList,
  fetchSmallBiddersOptionsList,
} from '~/src/dash/apiV2/uiOptions';

export async function queryHeaderBiddersByPublisherIDs(
  publisherIDs: string,
): Promise<IHeaderBidder[]> {
  const reqUrl =
    createUrl('/bidders_by_publisher_ids', '', 'v1') + '&publisher_ids=' + publisherIDs;
  const response = await restGet<any>(reqUrl, {});
  return response.parsedBody.headerbidders;
}

export const generateBiddersOptionsListV2 = (
  session: ISessionState,
  user: IUserState,
): Promise<ISelectOption[]> => {
  const shouldMaskLabel = checkUserInfoMasked(user.user);
  return fetchSmallBiddersOptionsList(session.publisher.ID)
    .then((bidders) => {
      if (!shouldMaskLabel) {
        return bidders;
      }
      return bidders.map((bidder) => ({
        ...bidder,
        label: `demo_bidder_${bidder.id}`,
      }));
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

interface IBidderGroupsResponse {
  BidderGroups: IBidderGroup[];
}

export async function fetchBidderGroups(publisherID: string): Promise<IBidderGroup[]> {
  const reqUrl = createUrl('/bidder_groups', publisherID, 'v1');
  const response = await restGet<IBidderGroupsResponse>(reqUrl, {});
  return (response.parsedBody as IBidderGroupsResponse).BidderGroups;
}

export const generateBidderGroupsOptionsList = (
  session: ISessionState,
  user: IUserState,
): Promise<ISelectOption[]> => {
  const shouldMaskLabel = checkUserInfoMasked(user.user);
  return fetchBidderGroups(session.publisher.ID)
    .then((bidderGroups) => {
      const options: ISelectOption[] = [];
      bidderGroups.forEach((bidderGroup) => {
        options.push({
          id: bidderGroup.id,
          label: shouldMaskLabel ? `demo_bidder_group_${bidderGroup.id}` : bidderGroup.Name,
        });
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const generateBidderGroupsOptionsListV2 = (
  session: ISessionState,
  user: IUserState,
): Promise<ISelectOption[]> => {
  const shouldMaskLabel = checkUserInfoMasked(user.user);
  return fetchSmallBidderGroupsOptionsList(session.publisher.ID)
    .then((bidderGroups) => {
      if (!shouldMaskLabel) {
        return bidderGroups;
      }
      return bidderGroups.map((bidderGroup) => ({
        ...bidderGroup,
        label: `demo_bidder_group_${bidderGroup.id}`,
      }));
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function fetchBidderLabels(publisherID: string): Promise<IBidderLabel[]> {
  const reqUrl = createUrl('/bidder_label', publisherID, 'v1');
  const response = await restGet<IBidderLabel[]>(reqUrl, {});
  return (response.parsedBody as IBidderLabel[]) || [];
}

export const generateBidderLabelsOptionsList = (
  session: ISessionState,
  user: IUserState,
): Promise<ISelectOption[]> => {
  return fetchBidderLabels(session.publisher.ID)
    .then((bidderLabels) => {
      const shouldMaskLabel = checkUserInfoMasked(user.user);
      return bidderLabels.map((label) => ({
        id: label.ID,
        label: shouldMaskLabel ? `demo_bidder_label_${label.ID}` : label.Name,
      }));
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export const BidderTierOptions: ISelectOption[] = [
  // { id: 11, label: 'Tier 0', tKey: 'TierNumber', tProps: { number: 0 } }, // only available for certain pubs. see fn below.
  { id: 10, label: 'Tier 1', tKey: 'TierNumber', tProps: { number: 1 } },
  { id: 9, label: 'Tier 2', tKey: 'TierNumber', tProps: { number: 2 } },
  { id: 8, label: 'Tier 3', tKey: 'TierNumber', tProps: { number: 3 } },
  { id: 7, label: 'Tier 4', tKey: 'TierNumber', tProps: { number: 4 } },
  { id: 6, label: 'Tier 5', tKey: 'TierNumber', tProps: { number: 5 } },
  { id: 5, label: 'Tier 6', tKey: 'TierNumber', tProps: { number: 6 } },
  { id: 4, label: 'Tier 7', tKey: 'TierNumber', tProps: { number: 7 } },
  { id: 3, label: 'Tier 8', tKey: 'TierNumber', tProps: { number: 8 } },
  { id: 2, label: 'Tier 9', tKey: 'TierNumber', tProps: { number: 9 } },
  { id: 1, label: 'Tier 10', tKey: 'TierNumber', tProps: { number: 10 } },
  { id: 0, label: 'Tier 11', tKey: 'TierNumber', tProps: { number: 11 } },
];

const isBidderTier0Enabled = (publisherID: string): boolean => {
  return [
    PID.samsungNA,
    PID.philo,
    PID.publica,
    PID.kennyTesting,
    PID.qaPublica,
    PID.elsonTestingV2,
  ].includes(publisherID); // Add publisher IDs that support Tier 0 here
};

export const generateBidderTierOptionsList = (session: ISessionState): Promise<ISelectOption[]> => {
  const options: ISelectOption[] = [];
  if (isBidderTier0Enabled(session.publisher.ID)) {
    options.push({ id: 11, label: 'Tier 0', tKey: 'TierNumber', tProps: { number: 0 } });
  }
  options.push(...BidderTierOptions);
  return new Promise((resolve) => resolve(options));
};
